import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Block, List, ListItem, Navbar, Page, Segmented, SegmentedButton, Tabbar, TabbarLink } from 'konsta/react';
import {
  BarChart3,
  Bell,
  CalendarDays,
  Heart,
  Home,
  LayoutDashboard,
  MapPin,
  MessageCircle,
  Search,
  ShieldBan,
  Sparkles,
  UserRound,
  Users,
  UsersRound,
  Gift,
  LifeBuoy,
  Pencil,
  Wallet as WalletIcon,
} from 'lucide-react';
import { TABS_BY_ROLE, type I18nKey, type Language, type Role } from '@harmony/shared';
import { api, type Me } from '../api';
import { haptic, requestLocation } from '../telegram';
import { useT } from '../i18n';
import { useNav } from '../nav';
import { getSocket } from '../realtime';
import { useToast } from '../components/ui';
import { HomeTab, OtherProvidersTab, SearchTab } from './client/Discover';
import { WalletPage } from './Wallet';
import { ChecklistCard, HelpPage, InvitePage, TutorialSheet } from './Growth';
import { EditClientPage } from './EditProfile';
import { ProviderProfileTab } from './provider/MyProfile';
import { AgentCard, AgentRequestsTab, MyProvidersTab, SubscriptionCard } from './agent/Agent';
import { isOn, useSettings } from '../settings';
import { Logo } from '../components/Logo';
import { BlockedPage, FavoritesPage } from './client/Lists';
import { ClientBookingsTab, ProviderRequestsTab } from './Bookings';
import { ChatScreen, ChatsTab } from './Chat';

const ICONS: Record<string, ReactNode> = {
  home: <Home size={24} />,
  search: <Search size={24} />,
  chats: <MessageCircle size={24} />,
  bookings: <CalendarDays size={24} />,
  profile: <UserRound size={24} />,
  requests: <Bell size={24} />,
  otherProviders: <UsersRound size={24} />,
  myProviders: <Users size={24} />,
  agentRequests: <BarChart3 size={24} />,
  dashboard: <LayoutDashboard size={24} />,
};

export interface DeepLink {
  tab?: string | null;
  chat?: string | null;
  page?: string | null;
}

/** Main app frame: a page per tab and an Instagram-style bottom tab bar for the user's role. */
export function Shell({ me, onMeChange, deepLink }: { me: Me; onMeChange: (me: Me) => void; deepLink?: DeepLink }) {
  const t = useT();
  const nav = useNav();
  const tabs = TABS_BY_ROLE[me.role as Role];
  const [active, setActive] = useState(deepLink?.tab && tabs.includes(deepLink.tab) ? deepLink.tab : tabs[0]);
  const [unread, setUnread] = useState(0);
  const [pending, setPending] = useState(0);
  const chats = me.role === 'CLIENT' || me.role === 'PROVIDER';

  const openChat = useCallback((id: string) => nav.push(`chat:${id}`, <ChatScreen id={id} />), [nav.push]);

  // Opened from a Telegram button: go straight to that chat or page.
  useEffect(() => {
    if (deepLink?.chat) openChat(deepLink.chat);
    else if (deepLink?.page === 'wallet' && me.role === 'CLIENT') nav.push('wallet', <WalletPage />);
  }, []);

  // Badge counts stay current through the live connection, with a slow refresh as backup.
  useEffect(() => {
    if (!chats) return;
    const refresh = () => {
      api.unread().then((r) => setUnread(r.unread)).catch(() => undefined);
      if (me.role === 'PROVIDER') {
        api.incoming().then((r) => setPending(r.filter((b) => b.status === 'PENDING' && Date.parse(b.expiresAt) > Date.now()).length)).catch(() => undefined);
      }
    };
    refresh();
    const s = getSocket();
    s?.on('chat:message', refresh);
    s?.on('chat:read', refresh);
    const id = window.setInterval(refresh, 30_000);
    return () => {
      s?.off('chat:message', refresh);
      s?.off('chat:read', refresh);
      window.clearInterval(id);
    };
  }, [me.role]);

  const badge: Record<string, number> = { chats: unread, requests: pending };
  let content: ReactNode;
  switch (active) {
    case 'home':
      content = <HomeTab />;
      break;
    case 'search':
      content = <SearchTab />;
      break;
    case 'chats':
      content = <ChatsTab openChat={openChat} onUnread={setUnread} />;
      break;
    case 'bookings':
      content = <ClientBookingsTab openChat={openChat} />;
      break;
    case 'requests':
      content = <ProviderRequestsTab openChat={openChat} onPendingCount={setPending} />;
      break;
    case 'otherProviders':
      content = <OtherProvidersTab />;
      break;
    case 'myProviders':
      content = <MyProvidersTab />;
      break;
    case 'agentRequests':
      content = <AgentRequestsTab />;
      break;
    case 'profile':
      content = <ProfileTab me={me} onMeChange={onMeChange} />;
      break;
    default:
      content = <ComingSoon />;
  }

  return (
    <Page>
      <Navbar title={active === 'home' ? <Logo size={30} /> : t(`tab.${active}` as I18nKey)} />
      {content}
      <TutorialSheet />
      <Tabbar labels icons className="fixed bottom-0 left-0">
        {tabs.map((tab) => (
          <TabbarLink
            key={tab}
            active={active === tab}
            onClick={() => {
              haptic('select');
              setActive(tab);
            }}
            icon={
              <span className="relative inline-flex">
                {ICONS[tab]}
                {!!badge[tab] && (
                  <span className="absolute -right-2.5 -top-1.5 min-w-[18px] rounded-full bg-red-500 px-1 text-center text-[11px] font-bold leading-[18px] text-white">
                    {badge[tab] > 99 ? '99+' : badge[tab]}
                  </span>
                )}
              </span>
            }
            label={t(`tab.${tab}` as I18nKey)}
          />
        ))}
      </Tabbar>
    </Page>
  );
}

function ComingSoon() {
  const t = useT();
  return (
    <div className="flex flex-col items-center justify-center gap-3 pt-32 text-tg-hint">
      <Sparkles size={40} />
      <p>{t('app.coming_soon')}</p>
    </div>
  );
}

function ProfileTab({ me, onMeChange }: { me: Me; onMeChange: (me: Me) => void }) {
  const t = useT();
  const nav = useNav();
  const toast = useToast();
  const settings = useSettings();
  const name = me.displayName ?? me.firstName ?? me.telegramUsername ?? '';
  const [locBusy, setLocBusy] = useState(false);

  const setLang = async (language: Language) => {
    if (language === me.language) return;
    haptic('select');
    onMeChange(await api.setLanguage(language));
  };

  const updateLocation = async () => {
    setLocBusy(true);
    const loc = await requestLocation();
    if (loc) {
      try {
        await api.updateClientLocation({ lat: loc.lat, lng: loc.lng });
        haptic('success');
        toast(t('profile.location_updated'));
      } catch {
        toast(t('error.generic'));
      }
    } else toast(t('reg.location_failed'));
    setLocBusy(false);
  };

  const Row = ({ icon, title, onClick }: { icon: ReactNode; title: string; onClick: () => void }) => (
    <ListItem link title={title} media={<span className="text-tg-link">{icon}</span>} onClick={onClick} />
  );

  const language = (
    <Block>
      <p className="mb-2 text-sm text-tg-hint">{t('profile.language')}</p>
      <Segmented strong rounded>
        <SegmentedButton active={me.language === 'en'} onClick={() => setLang('en')}>
          English
        </SegmentedButton>
        <SegmentedButton active={me.language === 'am'} onClick={() => setLang('am')}>
          አማርኛ
        </SegmentedButton>
      </Segmented>
    </Block>
  );
  const help = <Row icon={<LifeBuoy size={20} />} title={t('profile.help')} onClick={() => nav.push('help', <HelpPage />)} />;

  // Providers land straight on their own public profile.
  if (me.role === 'PROVIDER') {
    return (
      <ProviderProfileTab
        settings={
          <>
            <List strong inset className="!my-2">
              {help}
            </List>
            {language}
          </>
        }
      />
    );
  }

  return (
    <div className="pb-28">
      <div className="flex flex-col items-center gap-2 pt-6">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-tg-button text-3xl font-semibold text-tg-buttonText">
          {name.slice(0, 1).toUpperCase() || '🙂'}
        </div>
        <div className="text-xl font-semibold">{name}</div>
        <div className="text-tg-hint">
          {me.role ? t(`role.${me.role}` as I18nKey) : ''}
          {me.telegramUsername ? ` · @${me.telegramUsername}` : ''}
        </div>
        {me.role === 'CLIENT' && (
          <button
            onClick={() => nav.push('edit-client', <EditClientPage onSaved={() => api.me().then(onMeChange).catch(() => undefined)} />)}
            className="mt-1 flex items-center gap-1.5 rounded-full bg-tg-secondary px-5 py-2 text-sm font-semibold active:opacity-70"
          >
            <Pencil size={15} /> {t('profile.edit')}
          </button>
        )}
      </div>
      {me.role === 'AGENT' && (
        <>
          <SubscriptionCard />
          <AgentCard />
        </>
      )}
      <ChecklistCard />
      {me.role === 'CLIENT' && (
        <List strong inset>
          {isOn(settings, 'feature.client_wallet_enabled') && (
            <Row icon={<WalletIcon size={20} />} title={t('profile.wallet')} onClick={() => nav.push('wallet', <WalletPage />)} />
          )}
          <Row icon={<Heart size={20} />} title={t('profile.favorites')} onClick={() => nav.push('favorites', <FavoritesPage />)} />
          {isOn(settings, 'feature.referrals_enabled') && (
            <Row icon={<Gift size={20} />} title={t('profile.invite')} onClick={() => nav.push('invite', <InvitePage />)} />
          )}
          <Row icon={<MapPin size={20} />} title={locBusy ? '…' : t('profile.update_location')} onClick={updateLocation} />
          <Row icon={<ShieldBan size={20} />} title={t('profile.blocked')} onClick={() => nav.push('blocked', <BlockedPage />)} />
        </List>
      )}
      <List strong inset>
        {help}
      </List>
      {language}
    </div>
  );
}
